import { useCallback, useEffect, useRef, useState } from "react";
import { getNotesDetails } from "../service/notes.service";
import { updateUserInfo } from "../slices/authSlice";
import type { TNotes } from "../types/index.type";
import { POLLING_INTERVAL_AUDIO_STATUS } from "../utils/constants";
import { eventEmitter } from "../utils/eventEmitter";
import { isMaxTimedOut } from "../utils/recording.utils";
import { useAppDispatch } from "./redux-hooks";

// Optional external (e.g. Redux-backed) store. When provided, notes live there
// instead of in this hook's local state, so other components can read them.
export type TNotesStore = {
  notes: TNotes[];
  replaceNotes: (notes: TNotes[]) => void;
  mergeNote: (note: TNotes) => void;
};

export const useAudioStatusCheck = (initialNotes: TNotes[], store?: TNotesStore) => {
  const [localNotes, setLocalNotes] = useState<TNotes[]>(initialNotes);
  const notes = store ? store.notes : localNotes;
  const timeoutsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const dispatch = useAppDispatch();

  const notesRef = useRef(notes);
  notesRef.current = notes;
  const storeRef = useRef(store);
  storeRef.current = store;

  const isCheckingAudioStatus = useCallback((note: TNotes) => {
    return note.status !== 1 && note.status !== 2 && !isMaxTimedOut(note) && note.error_status === 0;
  }, []);

  const mergeNote = useCallback(
    (data: TNotes) => {
      const prevNote = notesRef.current.find((note) => note.id === data.id);
      if (prevNote && prevNote.status !== 1 && data.status === 1) {
        dispatch(updateUserInfo());
        eventEmitter.emit("noteUpdated", data);
      }
      if (storeRef.current) {
        storeRef.current.mergeNote(data);
      } else {
        setLocalNotes((prev) =>
          prev.map((prevNote) => (prevNote.id === data.id ? { ...prevNote, ...data } : prevNote)),
        );
      }
    },
    [dispatch],
  );

  const checkAudioStatus = useCallback(
    async (note: TNotes) => {
      try {
        const response = await getNotesDetails(note.id);
        const data = await response.data;
        mergeNote(data);
        if (isCheckingAudioStatus(data)) {
          // Set a new timeout for this note
          timeoutsRef.current[note.id] = setTimeout(() => checkAudioStatus(data), POLLING_INTERVAL_AUDIO_STATUS);
        }
        // If timed out (server says is_processing_timed_out=true), polling stops.
      } catch (error) {
        console.error("Error checking audio status:", error);
      }
    },
    [isCheckingAudioStatus, mergeNote],
  );

  useEffect(() => {
    for (const note of notes) {
      if (isCheckingAudioStatus(note) && !timeoutsRef.current[note.id]) {
        timeoutsRef.current[note.id] = setTimeout(() => checkAudioStatus(note), POLLING_INTERVAL_AUDIO_STATUS);
      }
    }

    return () => {
      Object.values(timeoutsRef.current).forEach(clearTimeout);
      timeoutsRef.current = {};
    };
  }, [notes, checkAudioStatus, isCheckingAudioStatus]);

  const updateNotes = useCallback((newNotes: TNotes[]) => {
    if (storeRef.current) {
      storeRef.current.replaceNotes(newNotes);
    } else {
      setLocalNotes(newNotes);
    }
    // Clear existing timeouts when notes are updated
    Object.values(timeoutsRef.current).forEach(clearTimeout);
    timeoutsRef.current = {};
  }, []);

  return { notes, updateNotes };
};
